// Small pieces the pipeline's views share: a client's letter mark, a next step's day as a chip, and a layer
// that closes on Esc or a press outside it.

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Check, X } from "lucide-react";
import { dateWords, dealName, dueWords, type DealData } from "../../../shared/pipeline";
import { cx } from "../../lib/util";

const MARKS = ["#c2410c", "#0f766e", "#7c3aed", "#0369a1", "#1d4ed8", "#b45309", "#475569", "#be185d", "#15803d", "#4338ca", "#a16207", "#0e7490"];

/** A client's letter on a color of its own, the same each time for the same name. */
export function Mark({ d, size = 22 }: { d: Pick<DealData, "company" | "contact">; size?: number }) {
  const name = dealName(d);
  let h = 0;
  for (const c of name.toLowerCase()) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  const letter = (name.match(/[\p{L}\p{N}]/u)?.[0] ?? "?").toUpperCase();
  return (
    <span className="pp-mark" style={{ background: MARKS[h % MARKS.length], width: size, height: size, fontSize: Math.round(size / 2) }} aria-hidden>
      {letter}
    </span>
  );
}

/** Where a deal stands, in a chip: its next step's day, or won or lost and when. */
export function When({ d, today }: { d: DealData; today: string }) {
  if (d.status === "won")
    return (
      <span className="chip is-ok">
        <Check size={10} strokeWidth={2.6} /> Won{d.closedAt ? ` ${dateWords(d.closedAt)}` : ""}
      </span>
    );
  if (d.status === "lost")
    return (
      <span className="chip is-bad">
        <X size={10} strokeWidth={2.6} /> Lost{d.closedAt ? ` ${dateWords(d.closedAt)}` : ""}
      </span>
    );
  const w = dueWords(d, today);
  if (!w) return null;
  return <span className={cx("chip", w.tone === "late" && "is-bad", w.tone === "none" && "is-bad", w.tone === "today" && "is-warn")}>{w.text}</span>;
}

/** A small layer beside what opened it, kept on screen; Esc or a press outside closes it. */
export function Pop({ at, onClose, className, children, width = 220, side = "right" }: { at: DOMRect; onClose: () => void; className?: string; children: ReactNode; width?: number; side?: "right" | "below" }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number }>({ left: at.right + 8, top: at.top });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const h = el.offsetHeight;
    const left = side === "below" ? Math.min(Math.max(10, at.right - width), window.innerWidth - width - 10) : Math.min(at.right + 8, window.innerWidth - width - 10);
    const top = side === "below" ? Math.min(at.bottom + 6, window.innerHeight - h - 10) : Math.max(10, Math.min(at.top, window.innerHeight - h - 10));
    setPos({ left, top });
  }, [at, width, side]);
  useEffect(() => {
    const down = (e: PointerEvent) => !ref.current?.contains(e.target as Node) && onClose();
    const key = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      onClose();
    };
    window.addEventListener("pointerdown", down, true);
    window.addEventListener("keydown", key, true);
    return () => {
      window.removeEventListener("pointerdown", down, true);
      window.removeEventListener("keydown", key, true);
    };
  }, [onClose]);
  return createPortal(
    <div ref={ref} className={cx("menu island", className)} style={{ ...pos, width }} data-esc-own role="menu">
      {children}
    </div>,
    document.querySelector(".board-root") ?? document.body,
  );
}

/** The color of a stage's bar: the won stage in the theme's good color, the rest along the theme's chart colors. */
export function stageColor(i: number, won?: boolean): string {
  if (won) return "var(--ok)";
  return ["var(--fg-4)", "var(--chart-2)", "var(--accent)", "var(--chart-3)", "var(--chart-4)", "var(--chart-5)"][i % 6];
}
